import axios from 'axios';
import Buefy from 'buefy';
import { createApp } from 'vue';
import UrlSync from '@/shared/mixins/urlSync';
import VueScrollTo from 'vue-scrollto';
import { errorResponse } from '@/shared/interceptors/apiErrorInterceptor';
import authInterceptor from '@/shared/interceptors/authInterceptor';
import AuthService from '@/shared/services/authService.ts';
import { monkeyPatchBTableSort } from '@/btableMonkeyPatch';
import { getAzureConfiguration, getWebClientConfig } from './appConfig';
import { router } from '@/router';
import {
    ensureLoggedIn,
    ensurePermissionsLoaded,
    redirectOldCode,
    requireAuthForRoutes,
    requireFeatureEnabled,
    rewriteLegacyRouteUrls,
    rewriteTitle
} from '@/router/routerHelper';
import CommonLibraryApp from '@/apps/CommonLibraryApp.vue';
import mailboxQueue from '@/headerMailbox';
import '../css/main.scss';
import '@mdi/font/scss/materialdesignicons.scss';
import { createPinia } from 'pinia';
import { IntervalState } from '@/shared/intervalState.ts';

/**
 * Loads the configuration and binds the AuthService to the Window.
 */
const webclientConfig = await getWebClientConfig();
const azureConfig = await getAzureConfiguration();
const auth = new AuthService(axios, azureConfig);
await auth.init();
window.authService = auth;
window.config = webclientConfig;
await window.authService.signIn();

const app = createApp(CommonLibraryApp);

const buefyOptions = {
    defaultTooltipType: 'is-info'
};
app.use(Buefy, buefyOptions);
monkeyPatchBTableSort(app);

// Axios
axios.interceptors.request.use(authInterceptor.request);
axios.interceptors.response.use(null, errorResponse);
app.config.globalProperties.$http = axios;

// Store Config
// Pinia
const pinia = createPinia();
app.use(pinia);

// Globally provided state
IntervalState.provide(app);

// Router
router.beforeEach(ensureLoggedIn);
router.beforeEach(ensurePermissionsLoaded);

// Legacy urls are rewritten before the remaining guards run, so that access checks and the document
// title are based on the page the user actually lands on. Running it after rewriteTitle would leave
// the title of the intermediate match behind, because rewriteTitle never clears an earlier title.
router.beforeEach(rewriteLegacyRouteUrls);

// Temporarily disabling role requirement while we debug affiliate user access
// router.beforeEach(requireCommonLibraryAccess);

router.beforeEach(requireAuthForRoutes);
router.beforeEach(requireFeatureEnabled);
router.beforeEach(rewriteTitle);
router.beforeEach(redirectOldCode);
app.use(router);

// Mixin
app.use(UrlSync);

// Directives
app.directive('scroll-to', VueScrollTo);

// Misc helpers
window.headerMailbox = window.headerMailbox || mailboxQueue();

app.mount('#app');
